// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo } from 'react';

import { bendInputKeys } from '@evolvemep/api-ts';

import { isNil, isNotNil } from 'helpers/isNotNil';
import {
  type BendCreatorFormValues,
  useBendCreatorFormContext,
} from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import {
  BEND_TYPE_STEP,
  type BendCreatorStepIndex,
  DIMENSIONS_STEP,
  REVIEW_STEP,
  MATERIAL_STEP,
} from 'modules/Shop/EvolveFabrication/BendCreator/Nav/bendCreatorSteps';
import { formatDimensionsSummary, formatMaterial } from 'modules/Shop/EvolveFabrication/BendCreator/Nav/stepValues';
import { useBendBuilder } from 'modules/Shop/EvolveFabrication/BendCreator/State/useBendBuilder';
import { useBendTypes } from 'modules/Shop/EvolveFabrication/BendCreator/useBendTypes';
import { useConduits } from 'modules/Shop/EvolveFabrication/useConduits';

export type StepStatus = {
  /** The step's values are all present. */
  complete: boolean;
  /** Finished with Next and still complete. Done steps show a check and their value in the step list. */
  done: boolean;
  /** One of the step's fields has a validation error. */
  hasError: boolean;
  /** The step's value as shown in the step list, or null when there is nothing to show yet. */
  value: string | null;
};

type FieldKey = keyof BendCreatorFormValues;

const MATERIAL_FIELDS: readonly FieldKey[] = ['conduitTypeId', 'conduitSizeId', 'materialStockLength'];
const DIMENSIONS_FIELDS: readonly FieldKey[] = [...bendInputKeys, 'maximizeLegLength', 'centerBend', 'benderProfile'];
const REVIEW_FIELDS: readonly FieldKey[] = ['partName', 'quantity'];

/** Returns the status of each Bend Creator step, indexed by step. */
export const useStepStatuses = (): Record<BendCreatorStepIndex, StepStatus> => {
  const { values, errors } = useBendCreatorFormContext();
  const { finishedSteps } = useBendBuilder();
  const { bendTypes } = useBendTypes();
  const { conduitTypes, conduitSizes } = useConduits();

  return useMemo(() => {
    const bendType = bendTypes?.find(({ name }) => name === values.bendType);
    const hasErrorIn = (fields: readonly FieldKey[]) => fields.some((field) => isNotNil(errors[field]));
    // The validation context puts errors that belong to no field on `bendType`. Once a bend type is picked,
    // those are about the bend's geometry, so they mark Dimensions. Before that, the error is the missing bend type.
    const bendTypeHasError = isNotNil(errors.bendType);

    const material = formatMaterial(values, { conduitTypes, conduitSizes });
    const dimensions = formatDimensionsSummary(values, bendType);
    const requiredDimensionsEntered =
      isNotNil(bendType) &&
      bendType.inputs.every(({ keyName, isRequired }) => !isRequired || isNotNil(values[keyName]));

    const withDone = (step: BendCreatorStepIndex, status: Omit<StepStatus, 'done'>): StepStatus => ({
      ...status,
      done: finishedSteps.has(step) && status.complete,
    });

    return {
      [MATERIAL_STEP]: withDone(MATERIAL_STEP, {
        complete: isNotNil(material),
        hasError: hasErrorIn(MATERIAL_FIELDS),
        value: material,
      }),
      [BEND_TYPE_STEP]: withDone(BEND_TYPE_STEP, {
        complete: isNotNil(values.bendType),
        hasError: bendTypeHasError && isNil(values.bendType),
        value: values.bendType,
      }),
      [DIMENSIONS_STEP]: withDone(DIMENSIONS_STEP, {
        complete: requiredDimensionsEntered && isNotNil(values.benderProfile),
        hasError: hasErrorIn(DIMENSIONS_FIELDS) || (bendTypeHasError && isNotNil(values.bendType)),
        value: dimensions,
      }),
      [REVIEW_STEP]: withDone(REVIEW_STEP, {
        complete: values.partName.trim() !== '' && isNotNil(values.quantity),
        hasError: hasErrorIn(REVIEW_FIELDS),
        value: values.partName.trim() !== '' ? `${values.partName} × ${values.quantity ?? 0}` : null,
      }),
    };
  }, [values, errors, finishedSteps, bendTypes, conduitTypes, conduitSizes]);
};
